import * as R from 'ramda';

import { Order, OrderType, Stock, Transaction, TransactionStatus } from '@/generated/graphql';
import { Currency, currencySymbols } from '@/helpers/currency';
import { getItemValue } from '@/hooks/useLocalState';

import { todayDate } from './date';
import { DividendListItem } from '@/hooks/useDividendsListTable';

export const formatPrice = (price: number, currency: Currency = 'EUR') => `${price} ${currencySymbols[currency]}`;

export const getEURValue = (value: number, currency: Currency) =>
  currency === 'EUR' ? value : value / getItemValue(`${todayDate}.exchangeRate`, 1);

export const calculateExpenses = ({ grossReturns, netReturns }: Transaction) =>
  (grossReturns as number) - (netReturns as number);

export const calculateValue = ({ transmissionValue, shares, stock }: Transaction) =>
  transmissionValue ?? getEURValue(stock.value * shares, stock.market.currency as Currency);

export const calculateYesterdayValue = ({ shares, stock }: Transaction) =>
  getEURValue(stock.previousClose * shares, stock.market.currency as Currency);

export const calculateTodayReturns = ({
  buyDate,
  buyPrice,
  shares,
  stock,
}: Pick<Transaction, 'buyDate' | 'buyPrice' | 'shares' | 'stock'>) => {
  const change = stock.lastValueAt.includes(buyDate) ? stock.value - buyPrice : stock.change;
  return getEURValue(change * shares, stock.market.currency as Currency);
};

export const calculatePortfolioWeight = (acquisitionValue: number, portfolioCost: number) =>
  (acquisitionValue / portfolioCost) * 100;

export const calculateReturnYield = ({ buyPrice, status, stock, shares, ...transaction }: Transaction) => {
  const currencyReturns = calculateCurrentYield({
    stock,
    shares,
    buyPrice,
    acquisitionValue: transaction.acquisitionValue,
  });
  const euroReturns = status === TransactionStatus.Closed ? transaction.grossReturns : currencyReturns.euroReturns;
  const returnYield = status === TransactionStatus.Closed ? transaction.returnYield : currencyReturns.euroYield;
  const hasEarnings = (euroReturns as number) >= 0;

  return { returnYield: returnYield || 0, grossReturns: euroReturns || 0, hasEarnings };
};

export const sumOrdersCommissions = ({ orders }: Transaction) =>
  (orders as Order[]).reduce((acc, { type, commissions }) => ({ ...acc, [type]: acc[type] + commissions }), {
    [OrderType.Buy]: 0,
    [OrderType.Sell]: 0,
  });

export const calculateCurrentYield = ({
  stock,
  dividends = [],
  acquisitionValue,
  buyPrice,
  shares,
}: {
  stock: Stock;
  dividends?: DividendListItem[];
  acquisitionValue: number;
  shares: number;
  buyPrice: number;
}) => {
  const exchangeRate = stock.market.currency !== 'EUR' ? getItemValue(`${todayDate}.exchangeRate`, 1) : 1;

  const dividendsReturns = dividends.reduce(
    (returns, dividend) => {
      const dividendNetReturns = dividend.amount - dividend.commissions;
      const dividendEuroReturns =
        dividend.currency !== 'EUR' ? dividendNetReturns / dividend.exchangeRate : dividendNetReturns;

      return { currency: returns.currency + dividendNetReturns, euro: returns.euro + dividendEuroReturns };
    },
    { currency: 0, euro: 0 }
  );

  const currencyValue = shares * stock.value;
  const currencyReturns = currencyValue - buyPrice * shares + dividendsReturns.currency;
  const euroReturns = currencyValue / exchangeRate - acquisitionValue + dividendsReturns.euro;
  const currencyReturnsPerShare = currencyReturns / shares;
  const euroReturnsPerShare = euroReturns / shares;
  const currencyYield = (currencyReturnsPerShare / buyPrice) * 100;
  const euroYield = (euroReturnsPerShare / buyPrice) * 100;

  return { euroReturns, euroYield, currencyReturns, currencyYield };
};

export const groupByStatus = R.groupBy<Transaction>(({ status }) => status);
